import React, { useState, useEffect, useRef } from "react";
import { ChevronDown, Upload, Search } from "lucide-react";
import { categoryAPI } from "../../services";
import { useSelector } from "react-redux";

const BrandForm = ({ brand, onSave, onCancel }) => {
  const [formData, setFormData] = useState({
    name: "",
    category_id: [],
    description: "",
    image: null,
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [imagePreview, setImagePreview] = useState(null);
  const [categories, setCategories] = useState([]);
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [hasNewImage, setHasNewImage] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredCategories, setFilteredCategories] = useState([]);
  const dropdownRef = useRef(null);
  const { isPartsURL, isAccessoriesURL, isGodownURL } = useSelector((state) => state.domain);
  const isEditing = !!brand;

  // Fetch categories from API
  const fetchCategories = async () => {
    setLoadingCategories(true);
    try {
      const response = await categoryAPI.getCategoriesList({
        page: 1,
        page_size: 100, // Get all categories
      });

      if (response && Array.isArray(response.results)) {
        setCategories(response.results);
        setFilteredCategories(response.results);
      } else if (response && Array.isArray(response)) {
        setCategories(response);
        setFilteredCategories(response);
      } else {
        setCategories([]);
        setFilteredCategories([]);
      }
    } catch (error) {
      console.error("Error fetching categories:", error);
      setCategories([]);
      setFilteredCategories([]);
    } finally {
      setLoadingCategories(false);
    }
  };

  // Load categories on component mount
  useEffect(() => {
    fetchCategories();
  }, []);

  useEffect(() => {
    if (brand) {
      setFormData({
        name: brand.name || "",
        category_id: brand.categories || [],
        description: brand.description || "",
        image: brand.image || null,
      });
      setImagePreview(brand.image || null);
      setHasNewImage(false);
    }
  }, [brand]);

  // Filter categories based on search term
  useEffect(() => {
    if (searchTerm.trim() === "") {
      setFilteredCategories(categories);
    } else {
      const filtered = categories.filter((category) =>
        category.name.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredCategories(filtered);
    }
  }, [searchTerm, categories]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };

    if (isDropdownOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isDropdownOpen]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setImagePreview(e.target.result);
      };
      reader.readAsDataURL(file);
      setFormData((prev) => ({ ...prev, image: file }));
      setHasNewImage(true);
      if (errors.image) setErrors((prev) => ({ ...prev, image: "" }));
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file && file.type.startsWith("image/")) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setImagePreview(e.target.result);
      };
      reader.readAsDataURL(file);
      setFormData((prev) => ({ ...prev, image: file }));
      setHasNewImage(true);
      if (errors.image) setErrors((prev) => ({ ...prev, image: "" }));
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleDropdownToggle = () => {
    setIsDropdownOpen(!isDropdownOpen);
    if (!isDropdownOpen) {
      setSearchTerm(""); // Clear search when opening dropdown
    }
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleCategorySelect = (category) => {
    setFormData((prev) => {
      const isSelected = prev.category_id.includes(category.id);
      return {
        ...prev,
        category_id: isSelected
          ? prev.category_id.filter((id) => id !== category.id)
          : [...prev.category_id, category.id],
      };
    });
    if (errors.category) setErrors((prev) => ({ ...prev, category: "" }));
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = "Brand name is required";
    if (formData.category_id.length === 0)
      newErrors.category = "At least one category is required";
    if (!formData.image && !isEditing)
      newErrors.image = "Brand image is required";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    setIsSubmitting(true);
    try {
      const cleanedData = {};

      // For new brands, send all required fields
      if (!isEditing) {
        cleanedData.name = formData.name.trim();
        cleanedData.category_id = formData.category_id;
        cleanedData.description = formData.description.trim();
        if (formData.image) {
          cleanedData.image = formData.image;
        }
      } else {
        // For editing, only send changed fields
        if (formData.name.trim() !== (brand.name || "")) {
          cleanedData.name = formData.name.trim();
        }
        const brandCategories = brand.categories || [];
        if (
          JSON.stringify(formData.category_id.sort()) !==
          JSON.stringify(brandCategories.sort())
        ) {
          cleanedData.category_id = formData.category_id;
        }
        if (formData.description.trim() !== (brand.description || "")) {
          cleanedData.description = formData.description.trim();
        }
        // Only send image if a new image file was actually selected
        if (hasNewImage && formData.image) {
          cleanedData.image = formData.image;
        }
      }

      await onSave(cleanedData);
    } catch (error) {
      console.error("Error saving brand:", error);
      alert("An error occurred while saving the brand.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="brand-modal-overlay">
      <div className="brand-modal-container no-scrollbar">
        <div className="brand-modal-content">
          <div className="brand-modal-header">
            <h2 className="brand-modal-title">
              {isEditing ? "Edit Brand" : "Add New Brands"}
            </h2>
            <p className="brand-modal-subtitle">
              {isEditing
                ? "Update brand information"
                : "Add new brands by filling the details below"}
            </p>
          </div>

          <form onSubmit={handleSubmit} className="brand-modal-form">
            <div className="brand-modal-body">
              <div className="brand-form-group">
                <label htmlFor="name" className="brand-form-label">
                  Brand Name *
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  className={`${isPartsURL
                      ? "brand-form-input-parts"
                      : isAccessoriesURL
                        ? "brand-form-input-acc"
                        : isGodownURL
                          ? "brand-form-input-godown"
                          : "brand-form-input"
                    } ${errors.name ? "brand-input-error" : ""}`}
                  placeholder="E.g. Apple"
                  required
                />
                {errors.name && (
                  <div className="brand-error-message">{errors.name}</div>
                )}
              </div>

              <div className="brand-form-group">
                <label htmlFor="category" className="brand-form-label">
                  Category *
                </label>
                <div className="custom-dropdown" ref={dropdownRef}>
                  <div
                    className={`${isPartsURL ? "dropdown-trigger-parts" : isAccessoriesURL ? "dropdown-trigger-acc" : isGodownURL ? "dropdown-trigger-godown" : "dropdown-trigger"} ${isDropdownOpen ? "open" : ""
                      } ${errors.category ? "brand-input-error" : ""}`}
                    onClick={handleDropdownToggle}
                  >
                    <span className="dropdown-selected">
                      {formData.category_id.length > 0
                        ? categories
                          .filter((c) => formData.category_id.includes(c.id))
                          .map((c) => c.name)
                          .join(", ")
                        : "Select categories"}
                    </span>
                    <ChevronDown
                      className={`dropdown-arrow ${isDropdownOpen ? "open" : ""
                        }`}
                      size={16}
                    />
                  </div>
                  {/* Checkbox Dropdown menu */}
                  {isDropdownOpen && (
                    <div className="dropdown-menu">
                      <div className="dropdown-search-container">
                        <div className="dropdown-search-input-wrapper">
                          <Search className="dropdown-search-icon" size={16} />
                          <input
                            type="text"
                            placeholder="Search categories..."
                            value={searchTerm}
                            onChange={handleSearchChange}
                            className={`${isPartsURL ? "dropdown-search-input-parts" : isAccessoriesURL ? "dropdown-search-input-acc" : isGodownURL ? "dropdown-search-input-godown" : "dropdown-search-input"}`}
                            onClick={(e) => e.stopPropagation()}
                          />
                        </div>
                      </div>
                      <div className="dropdown-options-container">
                        {loadingCategories ? (
                          <div className="dropdown-option">
                            <span>Loading categories...</span>
                          </div>
                        ) : filteredCategories.length === 0 ? (
                          <div className="dropdown-option">
                            <span>
                              {searchTerm
                                ? "No categories found"
                                : "No categories available"}
                            </span>
                          </div>
                        ) : (
                          filteredCategories.map((category) => (
                            <div
                              key={category.id || category.name}
                              className={`dropdown-option ${formData.category_id.includes(category.id)
                                  ? "selected"
                                  : ""
                                }`}
                              onClick={() => handleCategorySelect(category)}
                            >
                              <input
                                type="checkbox"
                                checked={formData.category_id.includes(
                                  category.id
                                )}
                                onChange={() => handleCategorySelect(category)}
                                className={`${isPartsURL ? "dropdown-checkbox-parts" : isAccessoriesURL ? "dropdown-checkbox-acc" : isGodownURL ? "dropdown-checkbox-godown" : "dropdown-checkbox"}`}
                                onClick={(e) => e.stopPropagation()}
                              />
                              <span>{category.name}</span>
                            </div>
                          ))
                        )}
                      </div>
                    </div>
                  )}
                </div>
                {errors.category && (
                  <div className="brand-error-message">{errors.category}</div>
                )}
              </div>

              <div className="brand-form-group">
                <label htmlFor="image" className="brand-form-label">
                  Brand Image {!isEditing && <span>*</span>}
                </label>
                <div
                  className={`${isPartsURL
                      ? "brand-upload-area-parts"
                      : isAccessoriesURL
                        ? "brand-upload-area-acc"
                        : isGodownURL
                          ? "brand-upload-area-godown"
                        : "brand-upload-area"
                    } ${errors.image ? "brand-upload-error" : ""} ${imagePreview ? "has-image" : ""
                    }`}
                  onDrop={handleDrop}
                  onDragOver={handleDragOver}
                  onClick={() => document.getElementById("image-input").click()}
                >
                  <input
                    id="image-input"
                    type="file"
                    accept="image/*"
                    onChange={handleFileChange}
                    className="brand-file-input"
                    required={!isEditing}
                  />
                  {imagePreview ? (
                    <div className="brand-image-preview">
                      <img
                        src={imagePreview}
                        alt="Brand logo preview"
                        className="brand-preview-img"
                      />
                      <div className="brand-change-overlay">
                        <button
                          type="button"
                          className={`${isPartsURL
                              ? "brand-change-btn-parts"
                              : isAccessoriesURL
                                ? "brand-change-btn-acc"
                                : isGodownURL
                                  ? "brand-change-btn-godown"
                                : "brand-change-btn"
                            }`}
                          onClick={(e) => {
                            e.stopPropagation();
                            document.getElementById("image-input").click();
                          }}
                        >
                          Change Image
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="brand-upload-content">
                      <Upload
                        className={`${isPartsURL
                            ? "brand-upload-icon-parts"
                            : isAccessoriesURL
                              ? "brand-upload-icon-acc"
                              : isGodownURL
                                ? "brand-upload-icon-godown"
                              : "brand-upload-icon"
                          }`}
                        size={32}
                      />
                      <p className="brand-upload-text">
                        {isEditing
                          ? "Click to change image or drag & drop"
                          : "The image should be JPG, PNG, WEBP format"}
                      </p>
                    </div>
                  )}
                </div>
                {errors.image && (
                  <div className="brand-error-message">{errors.image}</div>
                )}
              </div>

              <div className="brand-form-group">
                <label htmlFor="description" className="brand-form-label">
                  Description
                </label>
                <textarea
                  id="description"
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  className={`${isPartsURL
                      ? "brand-form-textarea-parts"
                      : isAccessoriesURL
                        ? "brand-form-textarea-acc"
                        : isGodownURL
                          ? "brand-form-textarea-godown"
                        : "brand-form-textarea"
                    }`}
                  rows="3"
                  placeholder="Mobile phone and smartphone"
                />
              </div>
            </div>

            <div className="brand-modal-footer">
              <button
                type="button"
                className="brand-cancel-btn"
                onClick={onCancel}
                disabled={isSubmitting}
              >
                Cancel
              </button>
              <button
                type="submit"
                className={`${isPartsURL
                    ? "brand-submit-btn-parts"
                    : isAccessoriesURL
                      ? "brand-submit-btn-acc"
                      : isGodownURL
                        ? "brand-submit-btn-godown"
                        : "brand-submit-btn"
                  }`}
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <span className="brand-spinner"></span>
                    Saving...
                  </>
                ) : isEditing ? (
                  "Update Brand"
                ) : (
                  "Add Brands"
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default BrandForm;
